import React, { useState } from 'react'
import styled from 'styled-components'
import { useDispatch } from 'react-redux'
import { Dialog } from '@mui/material'
import { toast } from 'react-toastify'
import * as estateDocumentsService from 'services/estateDocumentsService'
import { loadEstateFoldersRoutine } from 'ducks/estateDocuments/actions'
import { getApiErrors } from 'utils/errors'
import { font, ink, line, radius, surface, transition } from 'features/dashboard/tokens'

const CYAN = '#33becc'

const EstateFolderFormModal = ({ estateId, mode, folder, parentFolderId = null, parentFolderName = null, onClose }) => {
  const dispatch = useDispatch()
  const [name, setName] = useState(mode === 'rename' ? (folder?.name || '') : '')
  const [saving, setSaving] = useState(false)

  const isRename = mode === 'rename'
  const title = isRename
    ? 'Rename folder'
    : parentFolderName
      ? `New subfolder in "${parentFolderName}"`
      : 'New folder'

  const handleSubmit = async () => {
    const trimmed = name.trim()
    if (!trimmed || saving) return
    setSaving(true)
    try {
      if (isRename) {
        await estateDocumentsService.updateEstateFolder({ estateId, folderId: folder.id, name: trimmed })
        toast.success('Folder renamed.')
      } else {
        await estateDocumentsService.createEstateFolder({ estateId, name: trimmed, parentFolderId })
        toast.success('Folder created.')
      }
      dispatch(loadEstateFoldersRoutine({ estateId }))
      onClose()
    } catch (error) {
      const apiError = getApiErrors(error)
      if (apiError?.trim()) toast.error(apiError)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open onClose={saving ? undefined : onClose} maxWidth='xs' fullWidth>
      <Container>
        <Title>{title}</Title>
        <Field>
          <FieldLabel htmlFor='estate-folder-name'>Folder name</FieldLabel>
          <TextInput
            id='estate-folder-name'
            autoFocus
            value={name}
            onChange={event => setName(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') handleSubmit()
              if (event.key === 'Escape') onClose()
            }}
            placeholder='e.g. Fire safety'
            disabled={saving}
          />
        </Field>
        <Footer>
          <CancelButton type='button' onClick={onClose} disabled={saving}>
            Cancel
          </CancelButton>
          <SaveButton type='button' onClick={handleSubmit} disabled={saving || !name.trim()}>
            {saving ? 'Saving…' : isRename ? 'Save' : 'Create'}
          </SaveButton>
        </Footer>
      </Container>
    </Dialog>
  )
}

export default EstateFolderFormModal

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
  background: ${surface.card};
`

const Title = styled.h2`
  margin: 0;
  font-size: ${font.heading};
  font-weight: 700;
  color: ${ink.base};
`

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const FieldLabel = styled.label`
  font-size: ${font.caption};
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${ink.mute};
`

const TextInput = styled.input`
  width: 100%;
  min-height: 40px;
  padding: 9px 12px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.base};
  font-size: ${font.body};
  outline: none;
  box-sizing: border-box;
  transition: ${transition};

  &:focus {
    border-color: ${CYAN};
  }

  &::placeholder {
    color: ${ink.mute};
  }

  &:disabled {
    opacity: 0.6;
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
`

const CancelButton = styled.button`
  min-height: 40px;
  padding: 0 18px;
  border: 1px solid ${line.strong};
  border-radius: ${radius.sm};
  background: ${surface.card};
  color: ${ink.soft};
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};

  &:hover:not(:disabled) {
    color: ${ink.base};
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`

const SaveButton = styled.button`
  min-height: 40px;
  padding: 0 22px;
  border: none;
  border-radius: ${radius.sm};
  background: ${CYAN};
  color: #ffffff;
  font-size: ${font.body};
  font-weight: 600;
  cursor: pointer;
  transition: ${transition};

  &:hover:not(:disabled) {
    background: #2aa6b3;
  }

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`
